import React, { useEffect, useRef, useState } from "react";

import { Button, IconButton, TextField } from "@mui/material";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "ag-grid-enterprise";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import EditIcon from "@mui/icons-material/Edit";
import PrintIcon from "@mui/icons-material/Print";
import VisibilityIcon from "@mui/icons-material/Visibility";

import SideNav from "component/layouts/sideNav";
import {
  closureStatusRenderer,
  setCompanies,
  setIndents,
  setItemCategories,
  setItemList,
  setItems,
  setSites,
  setSupplierCategories,
  setSuppliers,
  setTaxes,
  setUoms,
  setUsers,
  siteCellRenderer,
  supplierCellRenderer,
  userCellRenderer,
} from "component/cellRenderers/renderers";
import {
  setAlertConfirmDeletion,
  setAlertTitleText,
} from "component/modal/alertModal";

import {
  ADD,
  CHANGE_STATUS,
  DELETE,
  GO,
  MATERIAL_MANAGEMENT,
  PURCHASE_ORDER_INDENT,
} from "resources/constant/strings/string";
import {
  LIGHT_GREY_BACKGROUND,
  PRUSSIAN_BLUE,
  SPANISH_GREEN,
  WHITE,
} from "resources/constant/colors/theme";
import {
  PATH_LOGIN,
  PATH_PURCHASE_ORDER,
  PATH_PURCHASE_ORDER_DETAIL_WITHOUT_PARAM,
} from "resources/constant/url/url";

import { APILOGIN } from "apihandler/login";
import { APIMASTER } from "apihandler/master";
import { APIMATERIALMGMT } from "apihandler/materialmgmt";

import {
  setFilters,
  setRows,
  saveGridState,
  setHydrated,
} from "store/Slice/purchaseOrdersSlice";


export default function PurchaseOrder() {
  const navigate = useNavigate();
  const gridRef = useRef();

  const dispatch = useDispatch();
  const {
    fromDate,
    toDate,
    rows,
    grid: gridState,
    hydrated,
  } = useSelector((s) => s.purchaseOrders);

  const { date } = useParams();

  const [selectedRowId, setSelectedRowId] = useState(0);
  const [selectedRowIds, setSelectedRowIds] = useState([]);

  const [dateValue, setDateValue] = useState(
    fromDate ? dayjs(fromDate) : dayjs().startOf("month")
  );
  const [toDateValue, setToDateValue] = useState(
    toDate ? dayjs(toDate) : dayjs().endOf("month")
  );

  useEffect(() => {
    if (!APILOGIN.GetIsLoggedIn()) navigate(PATH_LOGIN);
  }, [navigate]);

  const changeStatusFunction = () => {
    if (selectedRowId <= 0) {
      setAlertTitleText("Oops...", `Please select a row...`);
      return;
    }

    Swal.fire({
      title: "Change Status",
      input: "select",
      inputOptions: {
        P: "Pending",
        MC: "Manually Closed",
      },
      inputPlaceholder: "Select",
      confirmButtonText: "Save",
      showCancelButton: true,
    }).then((result) => {
      if (result.isConfirmed) {
        APIMATERIALMGMT.PurchaseOrderChangeStatus({
          status: result.value,
          ids: [selectedRowId],
        })
          .then((x) => x.json())
          .then((x) => {
            if (x.statusCode === 200) {
              setAlertTitleText("Status Changed", "");
              fetchOrders(dateValue, toDateValue).then((data) => {
                try {
                  const rowData = [];
                  gridRef.current.api.forEachNode(function (node) {
                    rowData.push(node.data);
                  });
                  gridRef.current.api.applyTransaction({
                    remove: rowData,
                  });
                } catch (e) {}
                try {
                  gridRef.current.api.applyTransaction({ add: data });
                  // keep redux cache in sync
                  dispatch(setRows(data));
                } catch (e) {}
              });
            } else {
              setAlertTitleText("Oops...", `Something went wrong...`);
            }
          });
      }
    });
  };

  const columnDefs = [
    {
      autoHeight: true,
      wrapText: true,
      flex: 1.5,
      headerName: "PO NO",
      field: "poNumber",
      checkboxSelection: false,
      cellStyle: {
        "border-right-color": "#e2e2e2",
      },
    },
    {
      flex: 1,
      headerName: "PO Date",
      field: "date",
      filter: false,
      floatingFilter: false,
      cellStyle: {
        "text-align": "center",
        "border-right-color": "#e2e2e2",
      },
      autoHeight: true,
      wrapText: true,
    },
    {
      headerName: "Supplier Name",
      field: "supplierId",
      autoHeight: true,
      wrapText: true,
      valueGetter: (params) =>
        supplierCellRenderer({ value: params.data.supplierId }),
      cellStyle: { "border-right-color": "#e2e2e2" },
    },
    {
      headerName: "Site",
      field: "siteId",
      autoHeight: true,
      wrapText: true,
      valueGetter: (params) => siteCellRenderer({ value: params.data.siteId }),
      cellStyle: { "border-right-color": "#e2e2e2" },
    },
    {
      headerName: "Grand Total",
      field: "grandTotal",
      filter: false,
      floatingFilter: false,
      cellStyle: {
        "text-align": "center",
        "border-right-color": "#e2e2e2",
      },
      autoHeight: true,
      wrapText: true,
    },
    {
      headerName: "GRN No",
      field: "grnNo",
      cellStyle: {
        "border-right-color": "#e2e2e2",
      },
      autoHeight: true,
      wrapText: true,
    },
    {
      headerName: "Status",
      field: "status",
      valueGetter: (params) =>
        closureStatusRenderer({ value: params.data.status }),
      cellStyle: { "border-right-color": "#e2e2e2" },
      autoHeight: true,
      wrapText: true,
    },
    {
      headerName: "Modified By",
      field: "modifiedBy",
      valueGetter: (params) =>
        userCellRenderer({ value: params.data.modifiedBy }),
      cellStyle: { "border-right-color": "#e2e2e2" },
      autoHeight: true,
      wrapText: true,
    },
    {
      flex: 1,
      headerName: "Details",
      field: "id",
      sortable: false,
      filter: false,
      floatingFilter: false,
      cellRenderer: (params) => (
        <div style={{ display: "flex", justifyContent: "center" }}>
          {(params.data.status === "P" || params.data.status === "N") && (
            <IconButton
              aria-label="edit"
              onClick={() => {
                captureGridState();
                navigate(
                  PATH_PURCHASE_ORDER_DETAIL_WITHOUT_PARAM + params.data.id,
                  {
                    state: {
                      date: (dateValue || dayjs()).format("YYYY-MM-DD"),
                      isEdit: true,
                    },
                  }
                );
              }}
            >
              <EditIcon style={{ color: PRUSSIAN_BLUE, width: "20px" }} />
            </IconButton>
          )}
          <IconButton
            aria-label="view"
            onClick={() => {
              captureGridState();
              navigate(
                PATH_PURCHASE_ORDER_DETAIL_WITHOUT_PARAM + params.data.id,
                {
                  state: {
                    date: (dateValue || dayjs()).format("YYYY-MM-DD"),
                    isEdit: false,
                  },
                }
              );
            }}
          >
            <VisibilityIcon style={{ color: PRUSSIAN_BLUE, width: "20px" }} />
          </IconButton>
          <IconButton
            aria-label="edit"
            onClick={() => {
              captureGridState();
              let filename = "";
              APIMATERIALMGMT.PurchaseOrderPrintPDF(params.data.id)
                .then((res) => {
                  setAlertTitleText("Please wait...", "Generating print...");
                  const contentType = res.headers.get("content-type");
                  if (
                    contentType &&
                    contentType.startsWith("application/json")
                  ) {
                    return Promise.reject(res.json());
                  } else {
                    const header = res.headers.get("Content-Disposition");
                    if (header !== null) {
                      const parts = header.split(";");
                      filename = parts[1].split("=")[1].replaceAll('"', "");
                    } else {
                      filename = "PurchaseOrder.html";
                    }

                    return res.blob();
                  }
                })
                .then((blob) => {
                  if (blob) {
                    blob.text().then((x) => {
                      let html = x.replace(
                        "<script>",
                        '<script type="text/javascript">'
                      );
                      let b = new Blob([html], { type: "text/html" });
                      let url = window.URL.createObjectURL(b, {
                        oneTimeOnly: true,
                      });
                      let win = window.open(url, "_blank", "");
                      setTimeout(() => {
                        win.print();
                      }, 2000);
                    });
                  }
                })
                .catch((m) => {
                  setAlertTitleText(
                    "Oops...",
                    `Something went wrong...${m.message}`
                  );
                });
            }}
          >
            <PrintIcon style={{ color: PRUSSIAN_BLUE, width: "20px" }} />
          </IconButton>
        </div>
      ),
    },
  ];

  const defaultColDef = {
    resizable: true,
    sortable: true,
    editable: false,
    flex: 1,
    filter: "agTextColumnFilter",
    filterParams: {
      caseSensitive: false,
      defaultOption: "startsWith",
    },
    floatingFilter: true,
    wrapHeaderText: true,
    autoHeaderHeight: true,
  };

  const fetchOrders = (d1, d2) => {
    return APIMATERIALMGMT.PurchaseOrders(
      d1.format("YYYY-MM-DD"),
      d2.format("YYYY-MM-DD")
    )
      .then((resp) => resp.json())
      .then((resp) => {
        if (resp.statusCode !== 200) {
          setAlertTitleText("Oops..", `An error occurred ${resp.message}`);
          return [];
        }
        const data = (resp.response || []).sort((a, b) => b.id - a.id);
        return data;
      });
  };

  const onGridReady = (params) => {
    const dFrom = fromDate
      ? dayjs(fromDate)
      : date
      ? dayjs(date)
      : dayjs().startOf("month");
    const dTo = toDate ? dayjs(toDate) : dayjs().endOf("month");

    setDateValue(dFrom);
    setToDateValue(dTo);

    APIMASTER.UomOptions()
      .then((r) => r.json())
      .then((r) => {
        if (r.statusCode === 200) setUoms(r.response);
      });
    APIMASTER.TaxOptions()
      .then((r) => r.json())
      .then((r) => {
        if (r.statusCode === 200) setTaxes(r.response);
      });
    APIMASTER.ItemCategories()
      .then((r) => r.json())
      .then((r) => {
        if (r.statusCode === 200) setItemCategories(r.response);
      });
    APIMASTER.SupplierCategories()
      .then((r) => r.json())
      .then((r) => {
        if (r.statusCode === 200) setSupplierCategories(r.response);
      });
    APIMATERIALMGMT.IndentOptions()
      .then((r) => r.json())
      .then((r) => {
        if (r.statusCode === 200) setIndents(r.response);
      });
    APIMASTER.SupplierOptions()
      .then((r) => r.json())
      .then((r) => {
        if (r.statusCode === 200) setSuppliers(r.response);
      });
    APIMASTER.SiteOptions()
      .then((r) => r.json())
      .then((r) => {
        if (r.statusCode === 200) setSites(r.response);
      });
    APIMASTER.UserOptions()
      .then((r) => r.json())
      .then((r) => {
        if (r.statusCode === 200) setUsers(r.response);
      });
    APIMASTER.CompanyOptions()
      .then((r) => r.json())
      .then((r) => {
        if (r.statusCode === 200) setCompanies(r.response);
      });
    APIMASTER.Items()
      .then((resp) => resp.json())
      .then((resp) => {
        if (resp.statusCode !== 200) return;
        const itemOptions = resp.response.map((el) => ({
          name: el.itemName,
          value: el.id,
          dependentvalue: el.categoryId,
        }));
        setItems(itemOptions);
        setItemList(resp.response);
      });

    const paint =
      rows && rows.length ? Promise.resolve(rows) : fetchOrders(dFrom, dTo);
    paint.then((data) => {
      params.api.applyTransaction({ add: data });
      dispatch(setRows(data));
      if (gridState?.filterModel)
        params.api.setFilterModel(gridState.filterModel);
      if (gridState?.columnState)
        params.columnApi.applyColumnState({
          state: gridState.columnState,
          applyOrder: true,
        });
      if (gridState?.quickFilterText)
        params.api.setQuickFilter(gridState.quickFilterText);
      if (gridState?.selectedIds?.length) {
        params.api.forEachNode((n) => {
          if (gridState.selectedIds.includes(n.data.id)) n.setSelected(true);
        });
      }
      if (gridState?.scrollTop != null) {
        const body = document.querySelector(".ag-body-viewport");
        if (body) body.scrollTop = gridState.scrollTop;
      }
      dispatch(setHydrated(true));
    });
  };

  const onRowSelectionChanged = (event) => {
    const selectedRows = event.api.getSelectedRows();
    if (selectedRows.length > 0) {
      setSelectedRowId(selectedRows[0].id);
      const ids = selectedRows.map((row) => row.id);
      setSelectedRowIds(ids);
      dispatch(saveGridState({ selectedIds: ids }));
    } else {
      setSelectedRowId(0);
      setSelectedRowIds([]);
      dispatch(saveGridState({ selectedIds: [] }));
    }
  };

  const captureGridState = () => {
    const api = gridRef.current?.api;
    const columnApi = gridRef.current?.columnApi;
    const body = document.querySelector(".ag-body-viewport");
    dispatch(
      saveGridState({
        filterModel: api?.getFilterModel() || null,
        columnState: columnApi?.getColumnState() || null,
        quickFilterText: api?.getQuickFilter?.() || "",
        selectedIds: (api?.getSelectedRows?.() || []).map((r) => r.id),
        scrollTop: body ? body.scrollTop : 0,
      })
    );
    dispatch(
      setFilters({
        fromDate: (dateValue || dayjs()).format("YYYY-MM-DD"),
        toDate: (toDateValue || dayjs()).format("YYYY-MM-DD"),
      })
    );
  };

  const deleteModel = () => {
    if (selectedRowIds.length === 0) return;

    setAlertConfirmDeletion(
      "Confirmation needed",
      `Do you confirm on proceeding to delete ${selectedRowIds.length} record(s)?`
    ).then((consent) => {
      if (!consent.isConfirmed) return;

      APIMATERIALMGMT.DeletePurchaseOrders(selectedRowIds)
        .then((resp) => resp.json())
        .then((data) => {
          if (data.statusCode === 204) {
            const selectedData = gridRef.current.api.getSelectedRows();
            gridRef.current.api.applyTransaction({ remove: selectedData });
            setAlertTitleText("Success", "The record was deleted successfully");
            const remaining = (rows || []).filter(
              (r) => !selectedRowIds.includes(r.id)
            );
            dispatch(setRows(remaining));
          } else {
            setAlertTitleText(
              "Oops...",
              `Something went wrong...${data.message}`
            );
          }
        });
    });
  };

  const onGo = () => {
    dispatch(
      setFilters({
        fromDate: (dateValue || dayjs()).format("YYYY-MM-DD"),
        toDate: (toDateValue || dayjs()).format("YYYY-MM-DD"),
      })
    );
    fetchOrders(dateValue, toDateValue).then((data) => {
      try {
        const rowData = [];
        gridRef.current.api.forEachNode((node) => rowData.push(node.data));
        gridRef.current.api.applyTransaction({ remove: rowData });
      } catch (e) {}
      try {
        gridRef.current.api.applyTransaction({ add: data });
        dispatch(setRows(data));
      } catch (e) {}
    });
  };

  return (
    <SideNav
      currentComponent={MATERIAL_MANAGEMENT}
      subComponent={PURCHASE_ORDER_INDENT}
      subComponentPath={PATH_PURCHASE_ORDER}
      detailComponent=""
    >
      <div
        style={{
          display: "flex",
          flexDirection: "row-reverse",
          marginBottom: 10,
          marginRight: 10,
        }}
      >
        <Button
          style={{
            backgroundColor: SPANISH_GREEN,
            textTransform: "capitalize",
            fontSize: 14,
            color: WHITE,
            minWidth: "180px",
            minHeight: "45px",
          }}
          onClick={() => {
            captureGridState();
            navigate(PATH_PURCHASE_ORDER_DETAIL_WITHOUT_PARAM + 0, {
              state: {
                date: (dateValue || dayjs()).format("YYYY-MM-DD"),
                isEdit: null,
              },
            });
          }}
        >
          {ADD}
        </Button>

        <Button
          onClick={deleteModel}
          variant="outlined"
          sx={{ borderColor: SPANISH_GREEN }}
          style={{
            textTransform: "capitalize",
            fontSize: 14,
            color: SPANISH_GREEN,
            minWidth: "180px",
            minHeight: "45px",
            marginRight: 10,
          }}
        >
          {DELETE}
        </Button>

        <Button
          style={{
            backgroundColor: SPANISH_GREEN,
            textTransform: "capitalize",
            fontSize: 14,
            color: WHITE,
            minWidth: "180px",
            minHeight: "45px",
            marginRight: 10,
          }}
          onClick={changeStatusFunction}
        >
          {CHANGE_STATUS}
        </Button>
      </div>

      {/* filters bar */}
      <div
        style={{
          backgroundColor: LIGHT_GREY_BACKGROUND,
          borderRadius: "5px",
          minWidth: "90%",
          minHeight: "50px",
          padding: 20,
          marginBottom: 10,
          borderStyle: "solid",
          border: "1x",
          borderColor: "#eaeaea",
          display: "flex",
        }}
      >
        <div style={{ display: "flex" }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DesktopDatePicker
              id="dateId"
              label="From Date"
              value={dateValue}
              onChange={(newValue) => setDateValue(newValue)}
              inputFormat="DD-MM-YYYY"
              renderInput={(params) => <TextField {...params} />}
            />
          </LocalizationProvider>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DesktopDatePicker
              id="toDateId"
              label="To Date"
              value={toDateValue}
              onChange={(newValue) => setToDateValue(newValue)}
              inputFormat="DD-MM-YYYY"
              renderInput={(params) => (
                <TextField {...params} style={{ marginLeft: 10 }} />
              )}
            />
          </LocalizationProvider>
        </div>
        <div
          align="right"
          style={{
            width: "100%",
            right: 0,
          }}
        >
          <Button
            style={{
              backgroundColor: SPANISH_GREEN,
              textTransform: "capitalize",
              fontSize: 14,
              color: WHITE,
              minWidth: "129px",
              minHeight: "45px",
              marginTop: 5,
            }}
            onClick={onGo}
          >
            {GO}
          </Button>
        </div>
      </div>

      {/* grid */}
      <div className="App">
        <div className="ag-theme-alpine" style={{ height: "570px" }}>
          <AgGridReact
            copyGroupHeadersToClipboard={true}
            enableRangeSelection={true}
            columnDefs={columnDefs}
            ref={gridRef}
            defaultColDef={defaultColDef}
            onGridReady={onGridReady}
            paginationAutoPageSize={true}
            pagination={true}
            rowSelection="multiple"
            getRowId={(params) => params.data.id}
            onSelectionChanged={onRowSelectionChanged}
            suppressBrowserResizeObserver={true}
          />
        </div>
      </div>
    </SideNav>
  );
}
